import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetTransportQuery,
  useGetDashboardOptionsQuery,
  applyVehicleCategoryColors,
  formatValue,
  type DashboardOptions
} from '@/entities/dashboard';
import { useUrlState, useProjectSiteParams } from '@/shared/lib';
import type { DateRangeValue } from '@/shared/ui';

import { buildTransportFilterChips } from '../model/build-filter-chips';
import { TransportUrlState } from '../model/types';

/** Transport-mode filter value meaning "all categories" — never sent to the API. */
const NO_VEHICLE_CATEGORY = 'NO';

const DEFAULT_URL_STATE: TransportUrlState = {
  metric: 'W',
  group: 'D',
  period: 'L6M',
  status: 'DI',
  breakDown: 'NO',
  vehicleCategoryId: NO_VEHICLE_CATEGORY,
  fromDate: '',
  toDate: ''
};

export function useTransportOverview() {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();
  const [urlState, setUrlState, resetUrlState] = useUrlState(DEFAULT_URL_STATE);
  const { vehicleCategoryId, ...filters } = urlState;

  const { data, isLoading, isFetching, error } = useGetTransportQuery({
    ...filters,
    projectId,
    siteId,
    ...(vehicleCategoryId && vehicleCategoryId !== NO_VEHICLE_CATEGORY ? { vehicleCategoryIds: vehicleCategoryId } : {})
  });

  const { data: optionsData } = useGetDashboardOptionsQuery({ projectId, siteId });

  // Colors are managed on the frontend: the API sends no series colors, the
  // vehicle-category palette is applied here (342405).
  const transportChart = useMemo(
    () => (data ? applyVehicleCategoryColors(data.transport, optionsData?.vehicleCategories) : undefined),
    [data, optionsData?.vehicleCategories]
  );

  const updateOption = useCallback(
    (key: keyof DashboardOptions, value: string) => {
      setUrlState({ [key]: value });
    },
    [setUrlState]
  );

  const handleDateRangeChange = useCallback(
    (dateRange: DateRangeValue) => {
      setUrlState(dateRange as TransportUrlState);
    },
    [setUrlState]
  );

  const removeFilter = useCallback(
    (key: string) => {
      if (key === 'date') {
        setUrlState({
          period: DEFAULT_URL_STATE.period,
          fromDate: DEFAULT_URL_STATE.fromDate,
          toDate: DEFAULT_URL_STATE.toDate
        });
        return;
      }

      setUrlState({ [key]: DEFAULT_URL_STATE[key as keyof TransportUrlState] });
    },
    [setUrlState]
  );

  const resetFilters = useCallback(() => resetUrlState(), [resetUrlState]);

  const activeFilters = useMemo(
    () =>
      buildTransportFilterChips({
        state: urlState,
        defaults: DEFAULT_URL_STATE,
        t,
        vehicleCategories: optionsData?.vehicleCategories,
        onRemove: removeFilter
      }),
    [urlState, t, optionsData?.vehicleCategories, removeFilter]
  );

  const activeFiltersCount = activeFilters.length;

  const formattedStats = data
    ? {
        totalShipments: data.totalNumberOfShipments.toLocaleString(),
        totalTransported: formatValue(data.totalTransported, urlState.metric),
        totalDestinations: data.totalNumberOfDestinations.toString(),
        totalLoss: formatValue(data.totalLoss, urlState.metric),
        totalDamage: formatValue(data.totalDamage, urlState.metric)
      }
    : null;

  return {
    // Data
    data,
    transportChart,
    formattedStats,
    vehicleCategories: optionsData?.vehicleCategories,

    // Loading states
    isLoading,
    isFetching,
    error,

    // Filters
    urlState,
    activeFilters,
    activeFiltersCount,
    hasActiveFilters: activeFiltersCount > 0,

    // Handlers
    updateOption,
    handleDateRangeChange,
    removeFilter,
    resetFilters,

    // Project/Site
    projectId,
    siteId
  };
}
